import { showToast } from '@finn/design-system/atoms/toast';
import {
  Addon,
  addonsFetcher,
  AddonsResponse,
  getAppliedInsuranceAddon,
  getInsuranceAddons,
} from '@finn/platform-modules';
import axios from 'axios';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

type InsuranceStore = {
  isInitialized: boolean;
  isUpdating: boolean;
  savedInsuranceAddon?: Addon;
  savedInsuranceAddonId?: string;
  shouldAutoExpandInsuranceStep: boolean;
  shouldShowInsuranceStep: boolean;
  errorAttachingInsurance?: boolean;
  errorUpdatingAdditionalProtection?: boolean;
  insuranceAddons?: Addon[];
  initialize: ({
    vehicleId,
    dealId,
  }: {
    vehicleId: string | number;
    dealId: string | number;
  }) => void;
  updateAdditionalProtectionOptions: ({
    dealId,
    addonsToAttach,
    addonsToRemove,
  }: {
    dealId: string | number;
    addonsToAttach: string[];
    addonsToRemove: string[];
  }) => Promise<void>;
  updateInsuranceOption: ({
    dealId,
    addonId,
  }: {
    dealId: string | number;
    addonId?: string;
  }) => Promise<void>;
};

export const useInsuranceStore = create<InsuranceStore>()(
  devtools(
    immer((set) => ({
      insuranceAddons: [] as Addon[],
      isInitialized: false,
      isUpdating: false,
      shouldAutoExpandInsuranceStep: false,
      shouldShowInsuranceStep: true,
      initialize: async ({
        vehicleId,
        dealId,
      }: {
        vehicleId: string | number;
        dealId: string | number;
      }) => {
        let addonsData: AddonsResponse | null = null;
        if (vehicleId && dealId) {
          addonsData = await addonsFetcher({
            vehicleId,
            dealId,
          });
        }

        set((state) => {
          if (!addonsData) {
            state.shouldShowInsuranceStep = false;

            return;
          }
          state.insuranceAddons = getInsuranceAddons(addonsData);
          const savedInsuranceAddon = getAppliedInsuranceAddon(addonsData);
          state.savedInsuranceAddon = savedInsuranceAddon;
          state.savedInsuranceAddonId = savedInsuranceAddon?.addon_id;

          if (!savedInsuranceAddon) {
            state.shouldAutoExpandInsuranceStep = true;
          }
          state.isInitialized = true;
        });
      },
      updateInsuranceOption: async ({ dealId, addonId }) => {
        set((state) => {
          state.isUpdating = true;
        });
        try {
          await axios.post('/api/addons/attach', {
            subscriptionId: dealId,
            addonId,
            isDeductible: true,
          });
          set((state) => {
            state.shouldAutoExpandInsuranceStep = false;
          });
        } catch (error) {
          console.error(error);
          showToast({
            description: 'Fehler beim Aktualisieren der Versicherung',
            status: 'error',
          });
          set((state) => {
            state.errorAttachingInsurance = true;
          });
        } finally {
          set((state) => {
            state.isUpdating = false;
            state.isInitialized = false;
          });
        }
      },

      updateAdditionalProtectionOptions: async ({
        dealId,
        addonsToAttach,
        addonsToRemove,
      }) => {
        if (addonsToAttach?.length || addonsToRemove?.length) {
          set((state) => {
            state.isUpdating = true;
          });
        }
        try {
          const attachPromises = addonsToAttach.map((addonId) =>
            axios.post('/api/addons/attach', {
              subscriptionId: dealId,
              addonId,
              isDeductible: true,
            })
          );

          const removePromises = addonsToRemove.map((addonId) =>
            axios.post('/api/addons/cancel', {
              subscriptionId: dealId,
              addonId,
            })
          );

          await Promise.all([...attachPromises, ...removePromises]);
        } catch (error) {
          console.error(error);
          showToast({
            description: 'Fehler beim Aktualisieren der Zusatzschutzoptionen',
            status: 'error',
          });
          set((state) => {
            state.errorUpdatingAdditionalProtection = true;
          });
        } finally {
          if (addonsToAttach?.length || addonsToRemove?.length) {
            set((state) => {
              state.isUpdating = false;
              state.isInitialized = false;
            });
          }
        }
      },
    })),
    { name: 'insuranceStore' }
  )
);
